import { useCallback, useMemo } from 'react';
import { SxProps, Theme } from '@mui/material';
import useUser from '../global/useUser';
import { isCashDiscountEnabled } from '../../helpers/payments';
import { LoadedUser } from '../../interfaces';
import {
  ONLINE_MIN_FEE_CENTS,
  TERMINAL_MIN_FEE_CENTS,
} from '../../constants/payments';

// Input style that visually attaches to a CashDiscountFormRow below it.
const ATTACHED_INPUT_SX: SxProps<Theme> = {
  borderBottomLeftRadius: '0 !important',
  borderBottomRightRadius: '0 !important',
  '& .MuiOutlinedInput-notchedOutline': {
    borderBottomLeftRadius: '0 !important',
    borderBottomRightRadius: '0 !important',
    borderWidth: '1px !important',
  },
};

const roundDollarAmount = (num: number) => Math.round(num * 100) / 100;

export interface GetAllInPriceOptions {
  /** Existing deposit (dollars). When set, fee splits into deposit + remaining portions with per-portion min-fee floor. */
  depositPrice?: number;
  /** Method for remaining amount. TERMINAL uses in-store rate; others use online. */
  paymentMethod?: string | null;
  /** How the deposit was paid. Drives rate/min-fee on the deposit portion. Ignored when `depositBundledFeeCents` is set. */
  depositPaymentMethod?: string | null;
  /** Deposit's bundled fee (cents) from the backend. When set, used directly — no client recalc. */
  depositBundledFeeCents?: number;
  /** Quantity for this line item. Defaults to 1. */
  quantity?: number;
  /** Sum of (price × quantity) for OTHER lines in the same cart group. When
   * set, the bundled fee is computed on the whole group base and this line's
   * share is proportional — matches the backend's group-level fee math. */
  groupOtherLinesTotal?: number;
}

export type GetAllInPrice = (
  price: number | undefined,
  options?: GetAllInPriceOptions,
) => number;

function getRateAndMinFee(
  paymentMethod: string | null | undefined,
  business: {
    inStoreCustomerServiceFeeRate?: number | null;
    onlineCustomerServiceFeeRate?: number | null;
  },
) {
  const isTerminal = paymentMethod === 'TERMINAL';
  const rate = isTerminal
    ? business.inStoreCustomerServiceFeeRate ?? 0
    : business.onlineCustomerServiceFeeRate ?? 0;
  const minFee =
    (isTerminal ? TERMINAL_MIN_FEE_CENTS : ONLINE_MIN_FEE_CENTS) / 100;
  return { rate, minFee };
}

function useCashDiscount() {
  const { user } = useUser<LoadedUser>();
  const { business } = user;

  const cashDiscountEnabled = isCashDiscountEnabled(business);

  const getAllInPrice = useCallback(
    (price: number | undefined, options: GetAllInPriceOptions = {}) => {
      const priceValue = price ?? 0;
      if (!business || !cashDiscountEnabled || priceValue <= 0)
        return roundDollarAmount(priceValue);

      const remainingRates = getRateAndMinFee(options.paymentMethod, business);
      const depositPrice = options.depositPrice ?? 0;
      const quantity = options.quantity ?? 1;
      const otherLinesTotal = options.groupOtherLinesTotal ?? 0;

      // This line's contribution to the group's fee-bearing base. Deposit
      // (per-line) subtracts; quantity multiplies.
      const thisLineFeeBase = Math.max(0, priceValue * quantity - depositPrice);
      const groupFeeBase = thisLineFeeBase + otherLinesTotal;
      const groupFee =
        groupFeeBase > 0 && remainingRates.rate > 0
          ? Math.max(groupFeeBase * remainingRates.rate, remainingRates.minFee)
          : 0;
      const thisLineShareTotal =
        groupFeeBase > 0 ? (thisLineFeeBase / groupFeeBase) * groupFee : 0;
      const thisLineSharePerUnit =
        quantity > 0 ? thisLineShareTotal / quantity : 0;

      let depositFee = 0;
      if (depositPrice > 0) {
        if (options.depositBundledFeeCents != null) {
          depositFee = options.depositBundledFeeCents / 100;
        } else {
          const depositRates = getRateAndMinFee(
            options.depositPaymentMethod,
            business,
          );
          depositFee =
            depositRates.rate > 0
              ? Math.max(depositPrice * depositRates.rate, depositRates.minFee)
              : 0;
        }
      }

      return roundDollarAmount(priceValue + thisLineSharePerUnit + depositFee);
    },
    [business, cashDiscountEnabled],
  );

  const getCashDiscountPrice = useCallback(
    (price: number) => roundDollarAmount(price),
    [],
  );

  const shouldRenderCashDiscount = useMemo(
    () =>
      !!business &&
      !!cashDiscountEnabled &&
      business.onlineCustomerServiceFeeRate !== 0,
    [business, cashDiscountEnabled],
  );

  const attachedInputSx = shouldRenderCashDiscount
    ? ATTACHED_INPUT_SX
    : undefined;

  return {
    business,
    cashDiscountEnabled,
    getAllInPrice,
    getCashDiscountPrice,
    shouldRenderCashDiscount,
    attachedInputSx,
  };
}

export default useCashDiscount;
